/**
 * Iter-291 — Private "Complete your profile" nudge for talents.
 *
 * Shown only inside the talent dashboard / profile editor when one or more
 * of the high-impact public-booking fields are empty: bio, portfolio
 * photos, gig roles. Certifications are *encouraged* but not required for
 * the nudge to dismiss (talents earn them via assessments, not direct edit).
 *
 * Never appears on the public booking page — clients should never see a
 * "talent is incomplete" warning.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const DISMISS_KEY = 'iter291.profile_complete_nudge.dismissedAt';
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7;  // 7 days

export default function ProfileCompletenessNudge() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Honor a recent dismissal so we don't nag every page-load.
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (ts && Date.now() - ts < DISMISS_TTL_MS) {
      setDismissed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/talent/profile-completeness');
        if (!cancelled) setData(r.data);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !data || data.is_complete) return null;

  const missing = data.missing_fields || [];

  return (
    <div
      className="rounded-2xl border border-cyan-200/70 bg-gradient-to-br from-cyan-50/80 via-white to-violet-50/60 shadow-[0_2px_18px_-12px_rgba(15,23,42,0.15)] p-4 sm:p-5"
      data-testid="profile-completeness-nudge"
    >
      <div className="flex items-start gap-3.5">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-500 text-white flex items-center justify-center shadow-sm">
          <Sparkles className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-semibold text-slate-900 leading-tight">
            Complete your profile to improve booking requests.
          </div>
          <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
            Add {humanizeMissing(missing)} so clients see your full premium
            profile when they open your booking link.
          </p>

          {missing.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {FIELD_LABELS.map(({ key, label }) =>
                missing.includes(key) ? (
                  <span
                    key={key}
                    className="text-[11.5px] font-medium px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600"
                    data-testid={`nudge-missing-${key}`}
                  >
                    {label}
                  </span>
                ) : (
                  <span
                    key={key}
                    className="text-[11.5px] font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"
                    data-testid={`nudge-done-${key}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> {label}
                  </span>
                )
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to="/talent/profile-info"
              className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-cyan-700 hover:text-cyan-800"
              data-testid="nudge-edit-link"
            >
              Edit profile <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <span className="text-slate-300">·</span>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem(DISMISS_KEY, String(Date.now()));
                setDismissed(true);
              }}
              className="text-[12px] text-slate-500 hover:text-slate-700"
              data-testid="nudge-dismiss"
            >
              Remind me later
            </button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
            setDismissed(true);
          }}
          className="shrink-0 text-slate-400 hover:text-slate-600"
          data-testid="nudge-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const FIELD_LABELS = [
  { key: 'profile_photo', label: 'Profile photo' },
  { key: 'bio',           label: 'Short bio' },
  { key: 'portfolio',     label: 'Portfolio photos' },
  { key: 'gig_roles',     label: 'Roles' },
  { key: 'certifications',label: 'Certifications' },
];

function humanizeMissing(missing) {
  const labels = FIELD_LABELS
    .filter(({ key }) => missing.includes(key))
    .map(({ label }) => label.toLowerCase());
  if (labels.length === 0) return 'a few finishing details';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}
